import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shiftsAPI, usersAPI, centersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format, startOfWeek, addDays } from 'date-fns';
import { fr, enCA } from 'date-fns/locale';
import i18n from '../i18n';
import AccessibleModal from '../components/common/AccessibleModal';

const ShiftModal = ({ shift, staff, centers, onClose, onSave, t }) => {
  const [form, setForm] = useState({
    userId: shift?.user_id || '',
    centerId: shift?.center_id || '',
    startTime: shift?.start_time ? format(new Date(shift.start_time), "yyyy-MM-dd'T'HH:mm") : '',
    endTime: shift?.end_time ? format(new Date(shift.end_time), "yyyy-MM-dd'T'HH:mm") : '',
    notes: shift?.notes || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (shift?.id) await shiftsAPI.update(shift.id, form);
      else await shiftsAPI.create(form);
      onSave();
    } catch (err) { setError(err.response?.data?.message || t('common.error')); }
    finally { setLoading(false); }
  };

  return (
    <AccessibleModal
      title={shift ? t('shifts.editShift') : t('shifts.addShift')}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" form="shift-form" className="btn btn-primary" disabled={loading}>{loading ? t('common.loading') : t('common.save')}</button>
        </>
      )}
    >
        <form id="shift-form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-error" role="alert">⚠ {error}</div>}
            <div className="form-group">
              <label className="form-label">{t('shifts.assignTo')} *</label>
              <select className="form-select" value={form.userId} onChange={e => setForm(f => ({ ...f, userId: e.target.value }))} required>
                <option value="">— {t('common.all')} —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('staff.center')} *</label>
              <select className="form-select" value={form.centerId} onChange={e => setForm(f => ({ ...f, centerId: e.target.value }))} required>
                <option value="">— {t('common.all')} —</option>
                {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('shifts.startTime')} *</label>
                <input type="datetime-local" className="form-input" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">{t('shifts.endTime')} *</label>
                <input type="datetime-local" className="form-input" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('patients.notes')}</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>
        </form>
    </AccessibleModal>
  );
};

const SchedulePage = () => {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canManageShifts = hasRole('admin', 'manager');
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [modalShift, setModalShift] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const locale = i18n.language.startsWith('fr') ? fr : enCA;

  const load = async () => {
    setLoading(true);
    try {
      const weekEnd = addDays(weekStart, 6);
      const jobs = [shiftsAPI.getAll({ start: weekStart.toISOString(), end: weekEnd.toISOString() })];
      if (canManageShifts) {
        jobs.push(usersAPI.getAll({ role: 'staff' }));
        jobs.push(centersAPI.getAll());
      }

      const [shiftsRes, staffRes, centersRes] = await Promise.all(jobs);
      setShifts(shiftsRes.data || []);
      setStaff(staffRes?.data?.users || []);
      setCenters(centersRes?.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [weekStart, canManageShifts]);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const getShiftsForDay = (day) =>
    shifts.filter(s => format(new Date(s.start_time), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd'));

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('shifts.title')}</h2>
          <p className="text-muted">
            {format(weekStart, 'd MMM', { locale })} – {format(addDays(weekStart, 6), 'd MMM yyyy', { locale })}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline" onClick={() => setWeekStart(d => addDays(d, -7))}>← Préc.</button>
          <button className="btn btn-outline" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>Aujourd'hui</button>
          <button className="btn btn-outline" onClick={() => setWeekStart(d => addDays(d, 7))}>Suiv. →</button>
          {canManageShifts && (
            <button className="btn btn-primary" onClick={() => { setModalShift(null); setShowModal(true); }}>+ {t('shifts.addShift')}</button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: 'var(--gray-200)' }}>
              {weekDays.map(day => (
                <div key={day.toISOString()} style={{ background: 'white', minHeight: 200 }}>
                  <div style={{
                    padding: '12px 12px 8px', borderBottom: '1px solid var(--gray-100)',
                    background: format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') ? 'var(--primary-50)' : 'var(--gray-50)'
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>
                      {format(day, 'EEE', { locale })}
                    </div>
                    <div style={{
                      fontSize: 20, fontWeight: 700,
                      color: format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') ? 'var(--primary)' : 'var(--gray-800)'
                    }}>
                      {format(day, 'd')}
                    </div>
                  </div>
                  <div style={{ padding: 8 }}>
                    {getShiftsForDay(day).map(shift => (
                      <div key={shift.id}
                        style={{
                          background: 'var(--primary-50)', border: '1px solid var(--primary-light)',
                          borderRadius: 6, padding: '6px 8px', marginBottom: 4, cursor: 'pointer',
                          fontSize: 11,
                        }}
                        onClick={() => canManageShifts && (setModalShift(shift), setShowModal(true))}
                      >
                        <div style={{ fontWeight: 600, color: 'var(--primary-dark)' }}>{shift.first_name} {shift.last_name}</div>
                        <div style={{ color: 'var(--gray-500)' }}>
                          {format(new Date(shift.start_time), 'HH:mm')}–{format(new Date(shift.end_time), 'HH:mm')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <ShiftModal
          shift={modalShift}
          staff={staff}
          centers={centers}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); load(); }}
          t={t}
        />
      )}
    </div>
  );
};

export default SchedulePage;
